import type { MasteryBannerLevel } from "../MasteryBanner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Minimal shape of a session used to evaluate mastery / regression triggers.
 * The full session payload may include more fields — this util only needs
 * the percent score per session.
 *
 * Sessions MUST be ordered chronologically, oldest first. The evaluator looks
 * at the trailing window (most recent N sessions) to decide the trigger.
 */
export interface MasterySession {
  /**
   * The session's percent score (0–100) used for both mastery and regression
   * comparisons. Skip null/undefined sessions — they don't contribute to the
   * consecutive count.
   */
  percent?: number | null;
}

/**
 * Unit the criterion's `threshold` is expressed in. Mirrors the builder's
 * `ThresholdUnit` — a target's threshold is only a percent for frequency
 * targets; duration/latency store seconds (or minutes) and magnitude stores a
 * rating, so copy must never assume "%".
 */
export type MasteryThresholdUnit = "percent" | "seconds" | "minutes" | "rating" | "occurrences";

/**
 * Mastery / regression criterion shape used by the evaluator.
 * Mirrors the JSONB stored on `Target.mastery_criteria` and
 * `Target.regression_criteria` (see API contract).
 *
 * `consecutive_points` defaults to 3 when omitted.
 * `threshold` is the score the session must hit, in `threshold_unit`
 * (percent when omitted, for backward compatibility).
 */
export interface MasteryBannerCriteria {
  consecutive_points?: number;
  threshold?: number;
  threshold_unit?: MasteryThresholdUnit;
}

/**
 * Clinical phase of the target (or the dominant phase of the goal).
 * Only `generalization` changes the copy wording.
 */
export type MasteryBannerPhase = "baseline" | "intervention" | "generalization" | "maintenance";

/**
 * Direction of a threshold comparison. `"above"` means a session *meets* the
 * criterion when its score is at or above the threshold (higher = better);
 * `"below"` means at or below (lower = better). This is the clinical-semantic
 * direction that MUST be supplied per criterion — it is NOT safe to assume
 * "more is better" (see behavior-reduction targets).
 */
export type MasteryDirection = "above" | "below";

const LATENCY_L1_DIMENSION = "latency" as const;
const BEHAVIOR_REDUCTION_TARGET_TYPE = "behavior-reduction" as const;

/**
 * Maps a target's L1 dimension + target type to the clinical comparison
 * direction for its mastery and regression criteria.
 *
 * Base direction: skill-acquisition wants the value to rise (mastery `above`,
 * regression `below`); behavior-reduction wants it to fall (mastery `below`,
 * regression `above`). Latency (`l1Dimension === "latency"`) is a *second*
 * inversion on top of that — responding faster is progress — so the two
 * flips cancel out for a latency + behavior-reduction target: mastery is
 * `above` (tolerating the problem behavior longer before it starts *is* the
 * goal) and regression is `below`. Only one of the two flips being active
 * inverts the base direction; both or neither active leaves it unchanged.
 *
 * Use this at the call site so the comparison direction is never an implicit
 * "more is better" assumption baked into the evaluator. Anything other than
 * `"behavior-reduction"` (including `null`/`undefined`) is treated as
 * skill-acquisition; anything other than `"latency"` is treated as non-latency.
 */
export function directionsForTarget(params: {
  l1Dimension?: string | null;
  targetType?: "skill-acquisition" | "behavior-reduction" | string | null;
}): { masteryDirection: MasteryDirection; regressionDirection: MasteryDirection } {
  const isLatency = params.l1Dimension === LATENCY_L1_DIMENSION;
  const isBehaviorReduction = params.targetType === BEHAVIOR_REDUCTION_TARGET_TYPE;
  const isInverted = isLatency !== isBehaviorReduction;
  return isInverted
    ? { masteryDirection: "below", regressionDirection: "above" }
    : { masteryDirection: "above", regressionDirection: "below" };
}

/**
 * The full result returned by `evaluateMasteryBanner`. When no trigger matches,
 * the function returns `null` and the caller should render no banner.
 */
export interface MasteryBannerEvaluation {
  level: MasteryBannerLevel;
  /** Title for the MasteryBanner. Omitted for `regression` (single-line layout). */
  title?: string;
  /** Detail text for the MasteryBanner. Omitted when there's no threshold to show. */
  detail?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_CONSECUTIVE_POINTS = 3;
const ALMOST_WINDOW = 3;
const ALMOST_HITS_REQUIRED = 2;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the trailing window of `size` sessions whose `percent` is a finite
 * number. Skips null/undefined entries so partial logs don't block triggers.
 */
function takeTrailingScored(sessions: MasterySession[], size: number): number[] {
  const scored: number[] = [];
  for (let i = sessions.length - 1; i >= 0 && scored.length < size; i -= 1) {
    const p = sessions[i].percent;
    if (typeof p === "number" && Number.isFinite(p)) scored.unshift(p);
  }
  return scored;
}

/**
 * Whether `value` meets `threshold` in the given direction: `"above"` →
 * `value >= threshold`, `"below"` → `value <= threshold` (both inclusive).
 */
function meets(value: number, threshold: number, direction: MasteryDirection): boolean {
  return direction === "above" ? value >= threshold : value <= threshold;
}

function countMeeting(values: number[], threshold: number, direction: MasteryDirection): number {
  return values.reduce((acc, v) => (meets(v, threshold, direction) ? acc + 1 : acc), 0);
}

/** Inclusive comparison phrase for copy, e.g. "at or above" / "at or below". */
export function atOrPhrase(direction: MasteryDirection): string {
  return direction === "above" ? "at or above" : "at or below";
}

/**
 * Renders a threshold with its unit for banner copy: `80%`, `26 sec`, `2 min`,
 * `4/5`, `12`. Symbol-ish suffixes (`%`, `/5`) attach to the number; word
 * suffixes get a space — matching the read-only criteria rows in the sidebar.
 *
 * Defaults to percent when the unit is unknown, preserving the copy that
 * frequency targets (the only ones that stored a threshold before) produced.
 */
export function formatThreshold(threshold: number, unit?: MasteryThresholdUnit): string {
  switch (unit) {
    case "seconds":
      return `${threshold} sec`;
    case "minutes":
      return `${threshold} min`;
    case "rating":
      return `${threshold}/5`;
    case "occurrences":
      return `${threshold}`;
    case "percent":
    default:
      return `${threshold}%`;
  }
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

function autoAdvancedCopy(
  consecutive: number,
  threshold: number,
  unit: MasteryThresholdUnit | undefined,
  direction: MasteryDirection,
  phase: MasteryBannerPhase | undefined
): MasteryBannerEvaluation {
  const isGen = phase === "generalization";
  return {
    level: "allTargetsMet",
    title: isGen
      ? `${consecutive} consecutive sessions at generalization criterion`
      : `${consecutive} consecutive sessions at criterion`,
    detail: `${consecutive} sessions ${atOrPhrase(direction)} ${formatThreshold(threshold, unit)}`,
  };
}

function almostMasteryCopy(
  threshold: number,
  unit: MasteryThresholdUnit | undefined,
  direction: MasteryDirection,
  phase: MasteryBannerPhase | undefined
): MasteryBannerEvaluation {
  const isGen = phase === "generalization";
  return {
    level: "approachingMastery",
    title: isGen
      ? `Approaching generalization — ${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} consecutive sessions at generalization criterion`
      : `Approaching mastery — ${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} consecutive sessions at criterion`,
    detail: `${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} sessions ${atOrPhrase(direction)} ${formatThreshold(threshold, unit)}`,
  };
}

function almostRegressionCopy(
  threshold: number,
  unit: MasteryThresholdUnit | undefined,
  direction: MasteryDirection
): MasteryBannerEvaluation {
  return {
    level: "approachingRegression",
    title: `Approaching regression — ${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} consecutive sessions ${atOrPhrase(direction)} threshold`,
    detail: `${ALMOST_HITS_REQUIRED} of ${ALMOST_WINDOW} sessions ${atOrPhrase(direction)} ${formatThreshold(threshold, unit)}`,
  };
}

function autoRegressedCopy(
  consecutive: number,
  threshold: number,
  unit: MasteryThresholdUnit | undefined,
  direction: MasteryDirection
): MasteryBannerEvaluation {
  // Per Figma — single-line layout (no title), red severity.
  return {
    level: "regression",
    detail: `${consecutive} consecutive sessions ${atOrPhrase(direction)} ${formatThreshold(threshold, unit)}`,
  };
}

// ---------------------------------------------------------------------------
// Main evaluator
// ---------------------------------------------------------------------------

/**
 * Determines which `MasteryBanner` (if any) should surface based on the most
 * recent session scores against the target's mastery / regression criteria.
 *
 * ## Rules (priority order)
 *
 * 1. **Auto-Advanced** — last N sessions all ≥ mastery threshold
 *    (N = `mastery.consecutive_points`, default 3). Severity success/green.
 *
 * 2. **Auto-Regressed** — last N sessions all ≤ regression threshold
 *    (N = `regression.consecutive_points`, default 3). Severity error/red,
 *    single-line layout (no title) per Figma.
 *
 * 3. **Almost-Mastery** — of the last 3 sessions, ≥ 2 hit the mastery
 *    threshold. Severity info/blue.
 *
 * 4. **Almost-Regression** — of the last 3 sessions, ≥ 2 hit the regression
 *    threshold. Severity warning/amber.
 *
 * Returns `null` if there aren't enough scored sessions or no trigger matches.
 *
 * ## Phase-aware copy
 *
 * When `phase === "generalization"` the Auto-Advanced and Almost-Mastery
 * banners swap "criterion" → "generalization criterion".
 *
 * ## Direction
 *
 * `masteryDirection` / `regressionDirection` set which way each threshold is
 * compared. Defaults match skill-acquisition targets (mastery `"above"`,
 * regression `"below"`). For behavior-reduction and/or latency targets pass
 * the resolved directions — use {@link directionsForTarget}. The comparison
 * is never an implicit "more is better" assumption.
 */
export function evaluateMasteryBanner(params: {
  sessions: MasterySession[];
  masteryCriteria?: MasteryBannerCriteria | null;
  regressionCriteria?: MasteryBannerCriteria | null;
  phase?: MasteryBannerPhase;
  masteryDirection?: MasteryDirection;
  regressionDirection?: MasteryDirection;
}): MasteryBannerEvaluation | null {
  const {
    sessions,
    masteryCriteria,
    regressionCriteria,
    phase,
    masteryDirection = "above",
    regressionDirection = "below",
  } = params;

  const masteryN = masteryCriteria?.consecutive_points ?? DEFAULT_CONSECUTIVE_POINTS;
  const masteryThreshold = masteryCriteria?.threshold;
  const masteryUnit = masteryCriteria?.threshold_unit;
  const regressionN = regressionCriteria?.consecutive_points ?? DEFAULT_CONSECUTIVE_POINTS;
  const regressionThreshold = regressionCriteria?.threshold;
  const regressionUnit = regressionCriteria?.threshold_unit;

  // -------------------------------------------------------------------------
  // 1. Auto-Advanced — last `masteryN` sessions all meet the mastery criterion
  // -------------------------------------------------------------------------
  if (typeof masteryThreshold === "number") {
    const window = takeTrailingScored(sessions, masteryN);
    if (
      window.length === masteryN &&
      window.every((v) => meets(v, masteryThreshold, masteryDirection))
    ) {
      return autoAdvancedCopy(masteryN, masteryThreshold, masteryUnit, masteryDirection, phase);
    }
  }

  // -------------------------------------------------------------------------
  // 2. Auto-Regressed — last `regressionN` sessions all meet the regression criterion
  // -------------------------------------------------------------------------
  if (typeof regressionThreshold === "number") {
    const window = takeTrailingScored(sessions, regressionN);
    if (
      window.length === regressionN &&
      window.every((v) => meets(v, regressionThreshold, regressionDirection))
    ) {
      return autoRegressedCopy(
        regressionN,
        regressionThreshold,
        regressionUnit,
        regressionDirection
      );
    }
  }

  // -------------------------------------------------------------------------
  // 3. Almost-Mastery — 2 of last 3 sessions meet the mastery criterion
  // -------------------------------------------------------------------------
  if (typeof masteryThreshold === "number") {
    const window = takeTrailingScored(sessions, ALMOST_WINDOW);
    if (
      window.length === ALMOST_WINDOW &&
      countMeeting(window, masteryThreshold, masteryDirection) >= ALMOST_HITS_REQUIRED
    ) {
      return almostMasteryCopy(masteryThreshold, masteryUnit, masteryDirection, phase);
    }
  }

  // -------------------------------------------------------------------------
  // 4. Almost-Regression — 2 of last 3 sessions meet the regression criterion
  // -------------------------------------------------------------------------
  if (typeof regressionThreshold === "number") {
    const window = takeTrailingScored(sessions, ALMOST_WINDOW);
    if (
      window.length === ALMOST_WINDOW &&
      countMeeting(window, regressionThreshold, regressionDirection) >= ALMOST_HITS_REQUIRED
    ) {
      return almostRegressionCopy(regressionThreshold, regressionUnit, regressionDirection);
    }
  }

  return null;
}
